// Jeton de connexion côté navigateur : où on le garde, comment on connaît
// son âge. Aucune dépendance (ni axios ni React) : testable seul sous Node.
//
// ── Pourquoi un jeton en plus du cookie ─────────────────────────────────────
// Le serveur pose un cookie httpOnly (SameSite=None; Secure), mais il n'est
// pas fiable sur iPhone : le site est servi par Vercel (terouma.fr) et l'API
// par une autre machine (api.terouma.fr), et Safari (ITP) limite ou ignore
// les cookies posés dans ce cas. On garde donc AUSSI le jeton reçu au login
// et l'intercepteur d'apiClient l'envoie en `Authorization: Bearer …`. Le
// serveur (auth.py:get_current_user) accepte l'un OU l'autre.
//
// ── Où on le garde ──────────────────────────────────────────────────────────
//   - sessionStorage (clé historique) : copie de l'onglet, lue en premier.
//   - localStorage (clé dédiée) : copie qui survit à la fermeture. Sans elle,
//     la web app installée sur l'écran d'accueil (Terouma Poche) perdait la
//     session À CHAQUE fermeture — iOS vide son sessionStorage — et il
//     fallait retaper le mot de passe à chaque ouverture.
// Contrepartie assumée : un script injecté pourrait lire le jeton plus
// longtemps. Garde-fous : contenus nettoyés (lib/sanitize.js), jeton jamais
// dans une URL ni dans un journal, et changer son mot de passe révoque côté
// serveur tous les jetons émis avant (auth.py:token_not_revoked). La
// déconnexion efface les deux copies, et les autres onglets ouverts se
// déconnectent aussi (isTokenClearedEvent) ; un renouvellement ne recrée
// jamais une copie effacée entre-temps (replaceAuthToken).
//
// Chaque accès au stockage est protégé : Safari en navigation privée, ou un
// stockage bloqué par les réglages, peut lever une exception — on dégrade
// sans bruit (le cookie reste tenté par le navigateur).

const SESSION_TOKEN_KEY = "terouma_auth_token";
const PERSISTENT_TOKEN_KEY = "terouma_auth_token_persistant";

// Âge au-delà duquel on demande un jeton neuf (POST /auth/renew), au
// démarrage ou au retour dans l'app (context/AuthContext.jsx).
// Le jeton vit 7 jours (backend/auth.py JWT_EXP_DAYS) : renouveler après 24 h
// laisse 6 jours de marge — ouvrir l'app une fois par semaine suffit.
export const RENEW_AFTER_SECONDS = 24 * 60 * 60;

// L'accès même à window.localStorage peut lever (stockage bloqué).
const storage = (name) => {
  try {
    return typeof window !== "undefined" ? window[name] || null : null;
  } catch {
    return null;
  }
};

const readKey = (name, key) => {
  try {
    return storage(name)?.getItem(key) || null;
  } catch {
    return null;
  }
};

const writeKey = (name, key, value) => {
  try {
    const store = storage(name);
    if (!store) return;
    if (value) store.setItem(key, value);
    else store.removeItem(key);
  } catch {
    // Quota plein, navigation privée… : l'autre copie ou le cookie prennent
    // le relais, rien ne doit casser.
  }
};

export const getAuthToken = () =>
  readKey("sessionStorage", SESSION_TOKEN_KEY)
  || readKey("localStorage", PERSISTENT_TOKEN_KEY);

// Écrit (ou efface, si token est vide) les DEUX copies d'un coup.
export const setAuthToken = (token) => {
  writeKey("sessionStorage", SESSION_TOKEN_KEY, token);
  writeKey("localStorage", PERSISTENT_TOKEN_KEY, token);
};

export const clearAuthToken = () => setAuthToken(null);

const STORES = [
  ["sessionStorage", SESSION_TOKEN_KEY],
  ["localStorage", PERSISTENT_TOKEN_KEY],
];

// Efface les copies qui valent `token` (et seulement elles) : un jeton refusé
// par le serveur ne doit pas emporter celui, valable, qu'un autre onglet a
// rangé entre-temps (reconnexion après un changement de mot de passe).
export const forgetAuthToken = (token) => {
  if (!token) return;
  STORES.forEach(([name, key]) => {
    if (readKey(name, key) === token) writeKey(name, key, null);
  });
};

// Renouvellement : remplace `previous` par `next` LÀ OÙ il est encore gardé,
// sans jamais recréer une copie effacée pendant l'appel (déconnexion ici ou
// dans un autre onglet, autre compte connecté) — sinon un renouvellement
// pourrait « annuler » une déconnexion. Renvoie vrai si une copie a changé.
export const replaceAuthToken = (previous, next) => {
  if (!previous || !next) return false;
  let replaced = false;
  STORES.forEach(([name, key]) => {
    if (readKey(name, key) === previous) {
      writeKey(name, key, next);
      replaced = true;
    }
  });
  return replaced;
};

// Événement `storage` (émis dans les AUTRES onglets du même site) : vrai si
// la copie persistante vient d'être effacée ET que cet onglet utilisait ce
// jeton-là (ou plus aucun) → déconnexion ailleurs, cet onglet doit suivre
// (sinon il garderait la session et la relancerait au prochain
// renouvellement). Un onglet connecté avec un AUTRE jeton, encore valable,
// n'est pas concerné. `currentToken` = getAuthToken() au moment de l'événement.
export const isTokenClearedEvent = (event, currentToken) =>
  Boolean(
    event
    && event.key === PERSISTENT_TOKEN_KEY
    && event.oldValue
    && !event.newValue
    && (!currentToken || currentToken === event.oldValue),
  );

// Date d'émission (iat, en secondes) lue dans la charge utile du JWT. La
// signature n'est PAS vérifiée : c'est au serveur de le faire ; ici on ne
// s'en sert que pour savoir s'il est temps de demander un jeton neuf.
// Renvoie null si le jeton est absent ou illisible.
export const tokenIssuedAt = (token) => {
  if (typeof token !== "string") return null;
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded));
    const iat = Number(claims?.iat);
    return Number.isFinite(iat) && iat > 0 ? iat : null;
  } catch {
    return null;
  }
};

// Vrai si le jeton a plus de RENEW_AFTER_SECONDS. Jeton illisible, ou daté
// dans le futur (horloge du téléphone décalée) → faux : on ne renouvelle pas.
export const shouldRenewToken = (token, nowMs = Date.now()) => {
  const iat = tokenIssuedAt(token);
  if (iat === null) return false;
  return nowMs / 1000 - iat > RENEW_AFTER_SECONDS;
};
